import React from 'react';
import { Form } from 'react-bootstrap';
import { BsArrowDown, BsArrowUp, BsArrowsFullscreen, BsGrid3X3Gap, BsImage, BsPalette, BsTextCenter } from 'react-icons/bs';
import ManagedImageField from './ManagedImageField';
import { presetOptionsForField } from '../config/presentationPresets';

const DISPLAY_OPTIONS = [
  { key: 'cover', label: 'Cover', icon: <BsArrowsFullscreen /> },
  { key: 'contain', label: 'Tile / Repeat', icon: <BsGrid3X3Gap /> },
];

const FOCUS_OPTIONS = [
  { key: 'center top', label: 'Top', icon: <BsArrowUp /> },
  { key: 'center', label: 'Center', icon: <BsTextCenter /> },
  { key: 'center bottom', label: 'Bottom', icon: <BsArrowDown /> },
];

function ChoiceGroup({ label, value, options, onChange }) {
  return (
    <div className="set-background-control">
      <span className="set-background-control-label">{label}</span>
      <div className="set-visual-selector" role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <button key={option.key} type="button" role="radio" aria-checked={value === option.key} className={value === option.key ? 'set-is-selected' : ''} onClick={() => onChange(option.key)}>
            {option.icon}<span>{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function BackgroundField({ fieldDef, values = {}, onChangeField, setId, onAssetUploaded }) {
  const imageKey = fieldDef.imageKey || 'background';
  const colorKey = fieldDef.colorKey || 'backgroundColor';
  const sizeKey = fieldDef.sizeKey || 'backgroundSize';
  const positionKey = fieldDef.positionKey || 'backgroundPosition';
  const darknessKey = fieldDef.darknessKey || 'backgroundDarkness';
  const tileSizeKey = fieldDef.tileSizeKey || 'backgroundTileSize';
  const modeKey = fieldDef.modeKey || 'backgroundMode';
  const imageValue = values[imageKey];
  const storedMode = values[modeKey];
  const mode = storedMode === 'color' || storedMode === 'image'
    ? storedMode
    : (imageValue ? 'image' : (fieldDef.defaultMode || 'color'));
  const presets = fieldDef.presetGroup ? presetOptionsForField(fieldDef.presetGroup) : [];
  const darkness = Number.isFinite(Number(values[darknessKey])) ? Number(values[darknessKey]) : Number(fieldDef.defaultDarkness ?? 30);
  const tileSize = Number.isFinite(Number(values[tileSizeKey])) ? Number(values[tileSizeKey]) : 50;
  const applyPresetDefaults = (preset) => {
    if (preset.display) onChangeField(sizeKey, preset.display);
    if (preset.focus) onChangeField(positionKey, preset.focus);
    if (Number.isFinite(Number(preset.tileSize))) onChangeField(tileSizeKey, Number(preset.tileSize));
  };

  return (
    <div className="set-background-field">
      <div className="set-background-source-tabs" role="tablist" aria-label="Background source">
        <button type="button" role="tab" aria-selected={mode === 'image'} className={mode === 'image' ? 'set-is-active' : ''} onClick={() => onChangeField(modeKey, 'image')}><BsImage /><span>Image</span></button>
        <button type="button" role="tab" aria-selected={mode === 'color'} className={mode === 'color' ? 'set-is-active' : ''} onClick={() => onChangeField(modeKey, 'color')}><BsPalette /><span>Color</span></button>
      </div>

      {mode === 'image' ? (
        <div className="set-background-image-panel">
          <ManagedImageField
            fieldDef={{ key: imageKey, title: 'Background image', type: presets.length ? 'image-selector' : 'image', options: presets, isBackground: true }}
            value={imageValue}
            onChange={(value) => onChangeField(imageKey, value)}
            onPresetSelect={applyPresetDefaults}
            setId={setId}
            onAssetUploaded={onAssetUploaded}
          />
          <div className="set-background-options-grid">
            <ChoiceGroup label="Display" value={values[sizeKey] || 'cover'} options={DISPLAY_OPTIONS} onChange={(value) => onChangeField(sizeKey, value)} />
            <ChoiceGroup label="Focus" value={values[positionKey] || 'center'} options={FOCUS_OPTIONS} onChange={(value) => onChangeField(positionKey, value)} />
            {(values[sizeKey] || 'cover') === 'contain' && <div className="set-background-control set-background-tile-size">
              <span className="set-background-control-label">Tile size</span>
              <div className="set-background-slider"><Form.Range min={10} max={100} step={5} value={tileSize} onChange={(event) => onChangeField(tileSizeKey, Number(event.target.value))} /><output>{tileSize}%</output></div>
            </div>}
            <div className="set-background-control set-background-darkness">
              <span className="set-background-control-label">Darkness</span>
              <div className="set-background-slider"><Form.Range min={0} max={90} step={5} value={darkness} onChange={(event) => onChangeField(darknessKey, Number(event.target.value))} /><output>{darkness}%</output></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="set-background-color-panel">
          <label htmlFor={`set-background-color-${colorKey}`}>Background color</label>
          <Form.Control id={`set-background-color-${colorKey}`} type="color" value={values[colorKey] || fieldDef.defaultColor || '#17191f'} onChange={(event) => onChangeField(colorKey, event.target.value)} className="set-color-input" />
          <span>Images and their settings are kept when you switch modes.</span>
        </div>
      )}
    </div>
  );
}
